import * as React from "react";
import { Link } from "react-router-dom";
import { FieldNav } from "../components/FieldNav.js";
import { FieldFooter } from "../components/FieldFooter.js";
import { useDocumentTitle } from "../hooks/useDocumentTitle.js";
import { SEO_TITLES } from "../lib/seo-titles.js";
import "../styles/landing-field.css";
import "../styles/content-pages-field.css";

/**
 * /features — an indexable hub for the product's distinctive capabilities, each
 * linking to where it actually lives. Captures the "what does it do" search
 * intent and, just as importantly, gives the feature surfaces (placement,
 * community quizzes, practice, test prep) another internal inbound link. Every
 * capability described here is real; every link points at a live page.
 */
export function FeaturesRoute(): React.ReactElement {
  useDocumentTitle(SEO_TITLES["/features"]);
  return (
    <div className="v-field">
      <FieldNav />
      <main id="main" className="v-field-c__page">
        <div className="container">
          <span className="v-field-c__eyebrow">Features</span>
          <h1 className="v-field-c__title">
            Everything a kid needs to <span className="stripe">win</span> at math.
          </h1>
          <p className="v-field-c__lede">
            Sports Academy Games wraps real K–12 curriculum in fast, competitive games — with a free
            placement test, standards-aligned practice, test prep, and student-made quizzes all in
            one place.
          </p>
          <div className="v-field-c__prose">
            <h2>A free adaptive placement test</h2>
            <p>
              Twelve adaptive questions, about five minutes, no card required — it pinpoints exactly
              which skills a student has and hasn&#39;t mastered, then tailors what every game
              serves. Start it from any <Link to="/games">game</Link> or{" "}
              <Link to="/practice">practice</Link> page.
            </p>
            <h2>Head-to-head math games</h2>
            <p>
              Every level is a real math problem dressed up as a 60–90 second head-to-head match.
              Browse the full <Link to="/games">math games arcade</Link> — kids drill because they
              want to win, not because they&#39;re told to.
            </p>
            <h2>Standards-aligned practice by grade</h2>
            <p>
              <Link to="/practice">Free math practice</Link> for Kindergarten through Grade 12, with
              worked answers and step-by-step explanations, organized by grade and skill and mapped
              to Common Core.
            </p>
            <h2>Standardized test prep</h2>
            <p>
              Game-based <Link to="/test-prep">math prep</Link> for the SAT, ACT, MAP, STAAR, and
              every U.S. state assessment — the same skills each test measures, drilled inside the
              games.
            </p>
            <h2>Community quizzes from your own notes</h2>
            <p>
              Turn any notes into a playable quiz, or browse thousands made by other students and
              teachers. Explore the <Link to="/community">community quizzes</Link> — a feature no
              other math-games site offers.
            </p>
          </div>
          <div className="v-field-c__actions">
            <Link className="btn-field-primary" to="/games">
              Browse the games
            </Link>
            <Link className="btn-field-secondary" to="/plans">
              See plans
            </Link>
          </div>
        </div>
      </main>
      <FieldFooter />
    </div>
  );
}
